
import React from 'react';
import { TransportationDetails } from '@/types/itinerary';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import TransportTypePicker from './form/TransportTypePicker';
import UserNameField from './form/UserNameField';
import LocationFields from './form/LocationFields';
import FlightNumberField from './form/FlightNumberField';
import DepartureFields from './form/DepartureFields';
import ConfirmationSection from './form/ConfirmationSection';

interface TransportationFormProps {
  details: TransportationDetails;
  onChange: (field: string, value: any) => void;
  onSave: () => void;
  formTitle: string;
  saveButtonText: string;
}

const TransportationForm: React.FC<TransportationFormProps> = ({ 
  details, 
  onChange, 
  onSave, 
  formTitle, 
  saveButtonText
}) => {
  // Initialize confirmations array if not present
  React.useEffect(() => {
    if (!details.confirmations) {
      onChange('confirmations', details.confirmation ? 
        [{ id: crypto.randomUUID(), value: details.confirmation }] : 
        []
      );
    }
  }, [details, onChange]);
  
  // Set default user name if not already set
  React.useEffect(() => {
    if (!details.userName) {
      const defaultName = localStorage.getItem('userName') || 'Anonymous';
      onChange('userName', defaultName);
    }
  }, [details.userName, onChange]);

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Validate required fields
    if (!details.departure || !details.arrival || !details.departureDate) {
      alert('Please fill in all required fields (Departure, Arrival, Date)');
      return;
    }
    onSave();
  };

  return (
    <form onSubmit={handleFormSubmit} className="space-y-4">
      <TransportTypePicker 
        selectedType={details.type} 
        onChange={(value) => onChange('type', value)} 
      />

      <UserNameField 
        userName={details.userName} 
        onChange={(value) => onChange('userName', value)} 
      />

      <LocationFields 
        departure={details.departure}
        arrival={details.arrival}
        onDepartureChange={(value) => onChange('departure', value)}
        onArrivalChange={(value) => onChange('arrival', value)}
      />

      {details.type === 'flight' && (
        <FlightNumberField 
          flightNumber={details.flightNumber || ''}
          onChange={(value) => onChange('flightNumber', value)}
        />
      )}

      <DepartureFields 
        departureDate={details.departureDate}
        departureTime={details.departureTime}
        onDateChange={(date) => onChange('departureDate', date)}
        onTimeChange={(value) => onChange('departureTime', value)}
      />

      <ConfirmationSection 
        confirmations={details.confirmations}
        onChange={onChange}
      />

      <div className="space-y-2">
        <Label htmlFor="notes">Notes</Label>
        <Input 
          id="notes" 
          value={details.notes}
          onChange={(e) => onChange('notes', e.target.value)}
          placeholder="Additional information"
        />
      </div>
      
      <Button 
        type="submit"
        className="w-full bg-glowingTeal hover:bg-navteal"
      >
        {saveButtonText}
      </Button>
    </form>
  );
};

export default TransportationForm;
